import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { quiz, paths } from '../data/village-data';
import ManifestorCard from './ManifestorCard';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ProfilePage() {
  const { user, token, logout, fetchProfile } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [journals, setJournals] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [newJournal, setNewJournal] = useState({ title: '', content: '' });
  const [newQuote, setNewQuote] = useState('');
  const [saving, setSaving] = useState(false);
  const [showCard, setShowCard] = useState(false);

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    loadData();
  }, [token]);

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const loadData = async () => {
    try {
      const [jRes, qRes] = await Promise.all([
        fetch(`${API}/api/profile/journal`, { headers }),
        fetch(`${API}/api/profile/quotes`, { headers }),
      ]);
      if (jRes.ok) setJournals(await jRes.json());
      if (qRes.ok) setQuotes(await qRes.json());
    } catch { /* silent */ }
  };

  const addJournal = async (e) => {
    e.preventDefault();
    if (!newJournal.title || !newJournal.content) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/profile/journal`, { method: 'POST', headers, body: JSON.stringify(newJournal) });
      if (res.ok) {
        const entry = await res.json();
        setJournals([entry, ...journals]);
        setNewJournal({ title: '', content: '' });
      }
    } catch { /* silent */ }
    setSaving(false);
  };

  const deleteJournal = async (id) => {
    await fetch(`${API}/api/profile/journal/${id}`, { method: 'DELETE', headers });
    setJournals(journals.filter(j => j.entry_id !== id));
  };

  const addQuote = async (e) => {
    e.preventDefault();
    if (!newQuote) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/profile/quotes`, { method: 'POST', headers, body: JSON.stringify({ text: newQuote }) });
      if (res.ok) {
        const entry = await res.json();
        setQuotes([entry, ...quotes]);
        setNewQuote('');
      }
    } catch { /* silent */ }
    setSaving(false);
  };

  const deleteQuote = async (id) => {
    await fetch(`${API}/api/profile/quotes/${id}`, { method: 'DELETE', headers });
    setQuotes(quotes.filter(q => q.quote_id !== id));
  };

  if (!user) return null;

  const archetype = user.archetype ? quiz.archetypes[user.archetype] : null;
  const visitedPaths = user.paths_visited || [];

  return (
    <section data-testid="profile-page" className="profile-page">
      {showCard && (
        <ManifestorCard
          name={user.name}
          archetypeKey={user.archetype}
          onClose={() => setShowCard(false)}
        />
      )}
      <nav className="profile-nav">
        <Link to="/life-map" className="path-back-link">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
          Back to Village
        </Link>
        <div className="profile-nav-right">
          {user.is_admin && (
            <button className="village-btn village-btn--secondary village-btn--sm" onClick={() => navigate('/admin')}>Admin</button>
          )}
          <button data-testid="logout-btn" className="village-btn village-btn--secondary village-btn--sm" onClick={() => { logout(); navigate('/'); }}>
            Sign Out
          </button>
        </div>
      </nav>

      <div className="profile-header">
        <div className="profile-avatar" data-testid="profile-avatar">
          <span>{user.name?.charAt(0)?.toUpperCase()}</span>
        </div>
        <h1 className="profile-name" data-testid="profile-name">{user.name}</h1>
        {archetype && <p className="profile-archetype" data-testid="profile-archetype">{archetype.name}</p>}
        {archetype && (
          <button data-testid="view-card-btn" className="village-btn village-btn--primary village-btn--sm mc-profile-btn" onClick={() => setShowCard(true)}>
            View My Manifestor Card
          </button>
        )}
        {!archetype && (
          <button className="village-btn village-btn--primary village-btn--sm" onClick={() => navigate('/quiz')}>
            Discover Your Archetype
          </button>
        )}
        <button
          data-testid="mirror-link-btn"
          className="village-btn village-btn--secondary village-btn--sm"
          onClick={() => navigate('/mirror')}
          style={{ marginTop: '0.5rem' }}
        >
          Enter The Mirror
        </button>
      </div>

      {/* Path Progress */}
      <div className="profile-progress">
        <h2 className="profile-section-title">Your Village Journey</h2>
        <p className="profile-progress-count">{visitedPaths.length} of {paths.length} paths explored</p>
        <div className="profile-paths-grid">
          {paths.map((p) => {
            const visited = visitedPaths.includes(p.id);
            return (
              <Link
                key={p.id}
                to={p.route}
                data-testid={`profile-path-${p.id}`}
                className={`profile-path-card ${visited ? 'profile-path-card--visited' : ''}`}
              >
                <span className="profile-path-status">{visited ? '~' : ''}</span>
                <span className="profile-path-name">{p.name}</span>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Tabs */}
      <div className="profile-tabs">
        <button data-testid="tab-journal" className={`profile-tab ${tab === 'journal' ? 'profile-tab--active' : ''}`} onClick={() => setTab('journal')}>
          Journal ({journals.length})
        </button>
        <button data-testid="tab-quotes" className={`profile-tab ${tab === 'quotes' ? 'profile-tab--active' : ''}`} onClick={() => setTab('quotes')}>
          Affirmations ({quotes.length})
        </button>
      </div>

      {/* Journal Tab */}
      {tab === 'journal' && (
        <div className="profile-tab-content" data-testid="journal-section">
          <form onSubmit={addJournal} className="journal-form">
            <input
              data-testid="journal-title-input"
              type="text"
              placeholder="Journal title..."
              value={newJournal.title}
              onChange={(e) => setNewJournal({ ...newJournal, title: e.target.value })}
              className="auth-input"
              required
            />
            <textarea
              data-testid="journal-content-input"
              placeholder="Write your thoughts..."
              value={newJournal.content}
              onChange={(e) => setNewJournal({ ...newJournal, content: e.target.value })}
              className="auth-input journal-textarea"
              rows={4}
              required
            />
            <button data-testid="journal-submit" type="submit" className="village-btn village-btn--primary village-btn--sm" disabled={saving}>
              Save Entry
            </button>
          </form>
          <div className="journal-list">
            {journals.map((j) => (
              <div key={j.entry_id} className="journal-entry" data-testid={`journal-${j.entry_id}`}>
                <div className="journal-entry-header">
                  <h3 className="journal-entry-title">{j.title}</h3>
                  <button className="journal-delete" onClick={() => deleteJournal(j.entry_id)} aria-label="Delete">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </div>
                <p className="journal-entry-content">{j.content}</p>
                <span className="journal-entry-date">{new Date(j.created_at).toLocaleDateString()}</span>
              </div>
            ))}
            {journals.length === 0 && <p className="empty-state">Your journal is empty. Start writing your story.</p>}
          </div>
        </div>
      )}

      {/* Quotes Tab */}
      {tab === 'quotes' && (
        <div className="profile-tab-content" data-testid="quotes-section">
          <form onSubmit={addQuote} className="quote-form">
            <input
              data-testid="quote-input"
              type="text"
              placeholder="Write an affirmation or personal quote..."
              value={newQuote}
              onChange={(e) => setNewQuote(e.target.value)}
              className="auth-input"
              required
            />
            <button data-testid="quote-submit" type="submit" className="village-btn village-btn--primary village-btn--sm" disabled={saving}>
              Add
            </button>
          </form>
          <div className="quotes-list">
            {quotes.map((q) => (
              <div key={q.quote_id} className="quote-card" data-testid={`quote-${q.quote_id}`}>
                <p className="quote-text">"{q.text}"</p>
                <button className="journal-delete" onClick={() => deleteQuote(q.quote_id)} aria-label="Delete">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
            ))}
            {quotes.length === 0 && <p className="empty-state">No affirmations yet. Speak your truth into existence.</p>}
          </div>
        </div>
      )}
    </section>
  );
}
